/**
 * articleShape — reviewer-language reading of the article-shape gate result.
 *
 * The Agent SDK writer records a machine verdict per article at
 * generation_metadata.quality_gate_result.agent_sdk_article_shape
 * (backend/template_content_crew/agent_sdk_writer/article_shape.py). Its
 * `errors` are developer strings ("H3 count 1 below minimum 2"). This pure
 * module turns them into rows the SEO review team reads at a glance.
 *
 * The pattern table mirrors SHAPE_ERROR_RULES in
 * backend/api/services/generation_health.py — same keys, same order — so the
 * per-article card and the fleet-wide health report name failures identically.
 *
 * Rules (same spirit as stepLabels.ts):
 * - An unrecognized error is NEVER dropped and never crashes: it falls back to
 *   the raw string as a plain statement under the `other` key.
 * - Numeric shortfalls carry measured/required so the card can render "6 / 8".
 */

/** Raw gate verdict as persisted in generation_metadata. */
export interface ArticleShapeReport {
  passed?: boolean;
  errors?: string[];
  warnings?: string[];
  metrics?: Record<string, unknown>;
}

/** One failed requirement, in reviewer language. */
export interface ShapeShortfall {
  /** Stable key (mirrors the backend rule key); `other` for unknown strings. */
  key: string;
  /** Plain-language requirement name, e.g. "H3 subheadings". */
  label: string;
  /** Measured value for numeric shortfalls, else null. */
  measured: number | null;
  /** Platform minimum for numeric shortfalls, else null. */
  required: number | null;
  /** The original backend string (kept for tooltips / debugging). */
  raw: string;
}

interface ShapeRule {
  key: string;
  pattern: RegExp;
  label: string;
}

/**
 * Ordered rule table. Order matters: the first match wins, so more specific
 * patterns precede their prefixes (e.g. the image-count rule precedes the
 * generic "generated image scaffold …" incomplete-scaffold rule).
 */
const SHAPE_ERROR_RULES: ShapeRule[] = [
  {
    key: 'word_count_below_min',
    pattern: /^word count (\d+) below production minimum (\d+)/i,
    label: 'Word count',
  },
  {
    key: 'top_nodes_below_min',
    pattern: /^top-level node count (\d+) below minimum (\d+)/i,
    label: 'Content blocks',
  },
  {
    key: 'heading_count_below_min',
    pattern: /^heading count (\d+) below minimum (\d+)/i,
    label: 'Headings',
  },
  {
    key: 'h2_count_below_min',
    pattern: /^H2 count (\d+) below minimum (\d+)/i,
    label: 'H2 sections',
  },
  {
    key: 'h3_count_below_min',
    pattern: /^H3 count (\d+) below minimum (\d+)/i,
    label: 'H3 subheadings',
  },
  {
    key: 'callout_count_below_min',
    pattern: /^callout count (\d+) below minimum (\d+)/i,
    label: 'Callout boxes',
  },
  {
    key: 'faq_item_count_below_min',
    pattern: /^FAQ item count (\d+) below minimum (\d+)/i,
    label: 'FAQ questions',
  },
  {
    key: 'image_scaffold_count_below_min',
    pattern: /^generated image scaffold count (\d+) below minimum\s+(\d+)/i,
    label: 'Images',
  },
  {
    key: 'image_scaffold_incomplete',
    pattern: /^generated image scaffold /i,
    label: 'An image is missing its prompt, caption or alt text',
  },
  {
    key: 'h4_not_allowed',
    pattern: /^H4\+ headings are not allowed/i,
    label: 'Uses H4 headings, which the article shape does not allow',
  },
  {
    key: 'heading_embedded_content',
    pattern: /^heading at top-level index \d+ contains embedded/i,
    label: 'A heading has body text pasted into it',
  },
  {
    key: 'toc_too_late',
    pattern: /^table-of-contents callout appears too late/i,
    label: 'Table of contents appears too far down the article',
  },
  {
    key: 'missing_toc_callout',
    pattern: /^missing table-of-contents callout/i,
    label: 'Missing table of contents',
  },
  {
    key: 'key_takeaways_too_late',
    pattern: /^key-takeaways callout appears too late/i,
    label: 'Key takeaways appear too far down the article',
  },
  {
    key: 'missing_key_takeaways_callout',
    pattern: /^missing key-takeaways callout/i,
    label: 'Missing key takeaways',
  },
  {
    key: 'missing_body_callout',
    pattern: /^missing at least one body\/citation callout/i,
    label: 'Missing a body or citation callout',
  },
  {
    key: 'missing_list_block',
    pattern: /^missing bulletList or orderedList block/i,
    label: 'Missing a bulleted or numbered list',
  },
  {
    key: 'table_too_early',
    pattern: /^table block appears too early/i,
    label: 'Table appears too early in the article',
  },
  {
    key: 'table_after_faq',
    pattern: /^table block must appear before the FAQ/i,
    label: 'Table appears after the FAQ instead of before it',
  },
  {
    key: 'missing_table_block',
    pattern: /^missing mid-article table block/i,
    label: 'Missing a mid-article table',
  },
  {
    key: 'missing_faq_schema',
    pattern: /^missing faqSchema block/i,
    label: 'Missing FAQ schema block',
  },
  {
    key: 'faq_schema_before_heading',
    pattern: /^faqSchema must appear after the FAQ heading/i,
    label: 'FAQ schema sits before the FAQ heading',
  },
  {
    key: 'missing_faq_heading',
    pattern: /^missing 'Frequently Asked Questions' heading/i,
    label: 'Missing FAQ heading',
  },
  {
    key: 'cta_after_faq',
    pattern: /^CTA heading must appear before the FAQ heading/i,
    label: 'CTA heading sits after the FAQ instead of before it',
  },
  {
    key: 'missing_cta_heading',
    pattern: /^missing CTA heading before FAQ/i,
    label: 'Missing CTA heading before the FAQ',
  },
  {
    key: 'visible_title_is_h1',
    pattern: /^visible title/i,
    label: 'Title carries a brand suffix that belongs in the meta title only',
  },
  {
    key: 'content_json_not_doc',
    pattern: /^content_json must be a TipTap doc/i,
    label: 'Article content is not a valid document',
  },
  {
    key: 'bundle_not_object',
    pattern: /^bundle is not a JSON object/i,
    label: 'Article content is not a valid document',
  },
];

/** Errors we cannot classify land here, so nothing is silently dropped. */
export const CATCH_ALL_KEY = 'other';

/**
 * Read the shape verdict out of a content item's generation_metadata.
 * Returns null when the article was generated by a path that never ran the
 * gate (older articles, non-Agent-SDK runs) — the caller shows a muted
 * "not checked" state rather than an alarming empty one.
 */
export function extractArticleShape(generationMetadata: unknown): ArticleShapeReport | null {
  if (!generationMetadata || typeof generationMetadata !== 'object') return null;
  const gate = (generationMetadata as Record<string, unknown>).quality_gate_result;
  if (!gate || typeof gate !== 'object') return null;
  const shape = (gate as Record<string, unknown>).agent_sdk_article_shape;
  if (!shape || typeof shape !== 'object' || Array.isArray(shape)) return null;

  const raw = shape as Record<string, unknown>;
  const errors = Array.isArray(raw.errors)
    ? raw.errors.filter((e): e is string => typeof e === 'string')
    : [];
  const metrics =
    raw.metrics && typeof raw.metrics === 'object' && !Array.isArray(raw.metrics)
      ? (raw.metrics as Record<string, unknown>)
      : {};
  return { passed: Boolean(raw.passed), errors, metrics };
}

/** Map one raw gate error onto a reviewer-facing shortfall. Never throws. */
export function parseShapeError(error: string): ShapeShortfall {
  const raw = (error ?? '').trim();
  for (const rule of SHAPE_ERROR_RULES) {
    const match = rule.pattern.exec(raw);
    if (!match) continue;
    const measured = match[1] !== undefined ? Number(match[1]) : null;
    const required = match[2] !== undefined ? Number(match[2]) : null;
    return { key: rule.key, label: rule.label, measured, required, raw };
  }
  // Unknown string: surface it verbatim as a plain statement.
  return { key: CATCH_ALL_KEY, label: raw, measured: null, required: null, raw };
}

/** Parse every gate error; tolerates undefined, non-arrays and non-strings. */
export function parseShapeErrors(errors: unknown): ShapeShortfall[] {
  if (!Array.isArray(errors)) return [];
  return errors
    .filter((e): e is string => typeof e === 'string' && e.trim().length > 0)
    .map(parseShapeError);
}
